/**
 * Standardized query key factories for Pinia Colada.
 *
 * Conventions:
 * - Keys are arrays starting with a feature prefix (e.g., 'sl', 'campaigns')
 * - Use `as const` for type safety and autocomplete
 * - Include ALL parameters that affect the query result
 * - Nested objects are compared by deep equality
 *
 * Benefits:
 * - Consistent cache key structure across the codebase
 * - Easy to invalidate related queries (e.g., all 'sl' queries)
 * - Type-safe key generation with autocomplete
 *
 * @see docs/guidelines/state-management.md
 *
 * @example
 * // In a query definition
 * useQuery({
 *   key: () => QueryKeys.sl.topicStats(orgId, topicId, filters),
 *   query: () => fetchTopicStats(...)
 * })
 *
 * @example
 * // Invalidating all SL queries
 * queryCache.invalidateQueries({ key: QueryKeys.sl.all(), exact: false })
 */

import type { PerformanceAlertDetails } from '@dashhudson/api-client/library/model';

import type { AdContentDetailIdentity, AdContentDetailScope } from '@/apis/ad-content-detail.types';

/**
 * Returns a new object with keys sorted alphabetically, so equivalent filter
 * payloads produce the same cache key regardless of property insertion order.
 * Undefined values are dropped to keep keys stable across optional params.
 */
function normalizeFilters(filters: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters)
      .filter(([, value]) => value !== undefined)
      .toSorted(([a], [b]) => a.localeCompare(b)),
  );
}

/**
 * Sorted copy of a requested metric set. Two sections declaring the same metrics in a different
 * order describe the same request, so they must land on the same cache entry rather than fetching
 * twice. Copies first — the caller's array is config, not ours to reorder.
 */
function sortMetrics(metrics: string[]): string[] {
  return [...metrics].toSorted();
}

/**
 * Reporting window for an analytics overview query. The comparison half is optional — channels
 * without a comparison period must key the same either way, which `normalizeFilters` handles by
 * dropping the undefined entries.
 */
type OverviewKeyRange = {
  startDate: string;
  endDate: string;
  contextStartDate?: string;
  contextEndDate?: string;
};

export const QueryKeys = {
  scheduledTasks: {
    listRoot: (viewerId: number) => ['scheduled-tasks', 'list', viewerId] as const,
    list: (viewerId: number, query: Record<string, unknown>) =>
      [...QueryKeys.scheduledTasks.listRoot(viewerId), normalizeFilters(query)] as const,
    sharesRoot: (viewerId: number) => ['scheduled-tasks', 'shares', viewerId] as const,
    shares: (viewerId: number, taskId: number) =>
      [...QueryKeys.scheduledTasks.sharesRoot(viewerId), taskId] as const,
    eligibleUsers: (viewerId: number, taskId: number, query: { search: string; limit: number }) =>
      ['scheduled-tasks', 'eligible-users', viewerId, taskId, normalizeFilters(query)] as const,
  },
  actionsNeeded: {
    listRoot: (viewerId: number) => ['actions-needed', 'list', viewerId] as const,
    list: (viewerId: number, query: Record<string, unknown>) =>
      [...QueryKeys.actionsNeeded.listRoot(viewerId), normalizeFilters(query)] as const,
  },
  taskRuns: {
    root: (viewerId: number) => ['task-runs', viewerId] as const,
    list: (viewerId: number, query: Record<string, unknown>) =>
      [...QueryKeys.taskRuns.root(viewerId), 'list', normalizeFilters(query)] as const,
    summary: (viewerId: number, taskId: number) =>
      [...QueryKeys.taskRuns.root(viewerId), 'summary', taskId] as const,
  },
  competitive: {
    overviewReports: () => ['competitive', 'overview-report'] as const,
    overviewReport: (request: Record<string, unknown> | null) =>
      [...QueryKeys.competitive.overviewReports(), normalizeFilters(request ?? {})] as const,
    industries: () => ['competitive', 'industries'] as const,
  },
  /**
   * Auth query keys.
   * Scoped to the current authenticated user unless otherwise noted.
   */
  auth: {
    /** Root key for all auth queries */
    all: () => ['auth'] as const,

    platformConnectionSettingsRoot: () => ['auth', 'platform-connection-settings'] as const,
    platformConnectionSettingsAccountsRoot: () =>
      ['auth', 'platform-connection-settings-accounts'] as const,
    platformConnectionBrandTags: (
      userId: number | null,
      organizationIds: number[],
      brandIds: number[],
    ) =>
      [
        'auth',
        'platform-connection-brand-tags',
        userId,
        [...organizationIds].toSorted((a, b) => a - b),
        [...brandIds].toSorted((a, b) => a - b),
      ] as const,

    platformConnectionSettings: (
      userId: number | null,
      brandIds: number[],
      params: Record<string, unknown>,
    ) =>
      [
        'auth',
        'platform-connection-settings',
        userId,
        [...brandIds].toSorted((a, b) => a - b),
        normalizeFilters(params),
      ] as const,

    platformConnectionSettingsAccounts: (userId: number | null, brandIds: number[]) =>
      [
        'auth',
        'platform-connection-settings-accounts',
        userId,
        [...brandIds].toSorted((a, b) => a - b),
      ] as const,

    /** Users across a set of brands (one batched request; key includes server-side filters) */
    brandUsers: (brandIds: number[], filters: Record<string, unknown> = {}) =>
      [
        'auth',
        'brand-users',
        [...brandIds].toSorted((a, b) => a - b),
        normalizeFilters(filters),
      ] as const,

    /** One user scoped to a brand */
    brandUser: (brandId: number, userId: number) =>
      ['auth', 'brand-user', brandId, userId] as const,

    /** OAuth authorizations for the current user */
    oauthAuthorizations: () => ['auth', 'oauth-authorizations'] as const,
  },

  /**
   * Social Listening query keys.
   * All SL queries are scoped by organization ID.
   */
  sl: {
    /** Root key for all SL queries - use for broad invalidation */
    all: () => ['sl'] as const,
    /** Topics list for an organization */
    topics: (orgId: number) => ['sl', 'topics', orgId] as const,

    /** Single topic details */
    topic: (orgId: number, topicId: number) => ['sl', 'topic', orgId, topicId] as const,

    /** Topic statistics with filters */
    topicStats: (orgId: number, topicId: number, filters: Record<string, unknown>) =>
      ['sl', 'topic-stats', orgId, topicId, filters] as const,

    /** Search results */
    search: (orgId: number, filters: Record<string, unknown>) =>
      ['sl', 'search', orgId, filters] as const,

    /** Parsed boolean search expression */
    parseTree: (orgId: number | null, expression: string) =>
      ['sl', 'parse-tree', orgId, expression] as const,

    /** Volume/trends data */
    volume: (orgId: number, topicId: number, params: Record<string, unknown>) =>
      ['sl', 'volume', orgId, topicId, params] as const,
  },

  /**
   * Creators query keys.
   * Most creator queries are scoped by brand ID.
   */
  creators: {
    /** Root key for all creator queries */
    all: () => ['creators'] as const,

    /** Influential fans with optional source and sort */
    influentialFans: (brandId: number, source?: string, sort?: string) =>
      ['creators', 'influential-fans', brandId, source, sort] as const,

    /** Creator details */
    detail: (brandId: number, creatorId: number) =>
      ['creators', 'detail', brandId, creatorId] as const,

    /** Creator matched by a platform handle, with custom details */
    byHandle: (brandId: number, platform: string, handle: string) =>
      ['creators', 'by-handle', brandId, platform, handle] as const,

    mediaMembershipRoot: (brandId: number) => ['creators', 'media-membership', brandId] as const,

    mediaMembership: (brandId: number, source: string, handle: string) =>
      ['creators', 'media-membership', brandId, source, handle] as const,

    /** Single creator-profile read, scoped by metric filters and an optional brand override */
    profile: (creatorId: number, params: object = {}, brandId?: number) =>
      brandId == null
        ? (['creators', 'profile', creatorId, JSON.stringify(params)] as const)
        : (['creators', 'profile', creatorId, JSON.stringify(params), brandId] as const),

    /** Prefix for all profile reads of a creator (cache walk / invalidation) */
    profileRoot: (creatorId: number) => ['creators', 'profile', creatorId] as const,

    /** Paginated "Your Creators" list for a brand, scoped by search params */
    yourCreators: (brandId: number, params: object) =>
      ['creators', 'your-creators', brandId, JSON.stringify(params)] as const,

    /** Prefix for all "Your Creators" list pages of a brand (cache walk / invalidation) */
    yourCreatorsRoot: (brandId: number) => ['creators', 'your-creators', brandId] as const,

    /** Count of saved creators for a brand (plan-limit progress bar) */
    count: (brandId: number) => ['creators', 'count', brandId] as const,

    /** All profile details needed to enrich a Vision creator-card payload. */
    visionCards: (brandId: number, creatorIds: number[]) =>
      ['creators', 'vision-cards', brandId, creatorIds] as const,

    /** Specific creators resolved by id (one-shot fetch) */
    byIds: (brandId: number, creatorIds: number[]) =>
      ['creators', 'by-ids', brandId, creatorIds] as const,

    /** Prefix for all by-ids reads of a brand (cache walk / invalidation) */
    byIdsRoot: (brandId: number) => ['creators', 'by-ids', brandId] as const,

    /** Full payee roster for a brand, drained page-by-page */
    payees: (brandId: number) => ['creators', 'payees', brandId] as const,

    /** Single payee by Trolley recipient id */
    payee: (brandId: number, payeeId: string) => ['creators', 'payee', brandId, payeeId] as const,

    /** Social account profile (IG or TikTok) */
    account: (brandId: number, source: string, handle: string, sourceCreatorId: string | null) =>
      ['creators', 'account', brandId, source, handle, sourceCreatorId] as const,

    /** Creator media list (infinite, URL-paged) */
    mediaPosts: (brandId: number, source: string, params: Record<string, unknown>) =>
      ['creators', 'media-posts', brandId, source, params] as const,

    /** Aggregate metrics for a filtered creator media list */
    mediaMetrics: (brandId: number, filters: object) =>
      ['creators', 'media-metrics', brandId, filters] as const,

    /** Account performance metrics */
    accountMetrics: (brandId: number, source: string, handle: string) =>
      ['creators', 'account-metrics', brandId, source, handle] as const,

    /** Avg weekly posts (slow endpoint, queried separately so it never blocks metrics) */
    accountAvgWeeklyPosts: (brandId: number, source: string, handle: string) =>
      ['creators', 'account-avg-weekly-posts', brandId, source, handle] as const,
  },

  /**
   * Campaigns query keys.
   * Most campaign queries are scoped by brand ID and/or campaign ID.
   */
  campaigns: {
    deliverablesRoot: (brandId: number, campaignId: number) =>
      [brandId, campaignId, 'campaign-deliverables'] as const,
    postsRoot: (brandId: number, campaignId: number) =>
      ['campaigns', 'posts', brandId, campaignId] as const,
    /** Root key for all campaign queries */
    all: () => ['campaigns'] as const,

    /** Campaigns list for a brand */
    list: (brandId: number) => ['campaigns', 'list', brandId] as const,

    /** Single campaign details */
    detail: (brandId: number, campaignId: number) =>
      ['campaigns', 'detail', brandId, campaignId] as const,

    /** Combined campaign posts, isolated by selection and saved draft snapshot. */
    posts: (
      brandId: number,
      campaignId: number,
      scope: {
        categories: string;
        sort: string;
        snapshotId?: string;
        snapshotAt?: number;
      },
    ) => ['campaigns', 'posts', brandId, campaignId, scope] as const,

    /** Campaign deliverables with optional params */
    deliverables: (brandId: number, campaignId: number, params?: Record<string, unknown>) =>
      [brandId, campaignId, 'campaign-deliverables', ...(params ? [params] : [])] as const,

    /** Collaboration presence for a campaign's creator selection and content filters. */
    creatorCollaborationPosts: (
      brandId: number,
      campaignId: number,
      params: Record<string, unknown>,
    ) => ['campaigns', 'creator-collaboration-posts', brandId, campaignId, params] as const,

    /** Campaign stats */
    stats: (brandId: number, campaignId: number, params?: Record<string, unknown>) =>
      ['campaigns', 'stats', brandId, campaignId, params] as const,

    /** Root for every topline metrics page of a campaign — use as an invalidation prefix */
    pageToplineMetricsRoot: (brandId: number, campaignId: number) =>
      [brandId, 'campaign-page-topline-metrics', campaignId] as const,

    /** Ad campaigns */
    adCampaigns: (brandId: number, campaignId: number) =>
      ['campaigns', 'ad-campaigns', brandId, campaignId] as const,

    adContent: (brandId: number, campaignId: number, params: Record<string, unknown>) =>
      ['campaigns', 'ad-content', brandId, campaignId, params] as const,

    /** Campaign payments */
    payments: (brandId: number, campaignId: number) =>
      ['campaigns', 'payments', brandId, campaignId] as const,

    /**
     * Campaigns fetched by ID — non-paginated bare-array response.
     * `ids` is sorted so equivalent selections share a cache entry.
     */
    byIds: (brandId: number, ids: readonly number[]) =>
      ['campaigns', 'by-ids', brandId, [...ids].toSorted((a, b) => a - b)] as const,
  },

  /**
   * Creator gifting query keys. Gifting data is scoped to a brand + campaign.
   */
  creatorGifting: {
    /** Root key for all creator-gifting queries */
    all: () => ['creator-gifting'] as const,

    /** Gifting collections for a campaign */
    list: (brandId: number, campaignId: number) =>
      ['creator-gifting', 'list', brandId, campaignId] as const,

    /** Campaign gifting settings (budget/deadline/orders-open) */
    giftDetail: (brandId: number, campaignId: number) =>
      ['creator-gifting', 'gift-detail', brandId, campaignId] as const,

    /** Every campaign creator with their gifting order */
    orders: (brandId: number, campaignId: number) =>
      ['creator-gifting', 'orders', brandId, campaignId] as const,

    /** A collection's stored products, hydrated with live SFCC detail */
    collectionProducts: (brandId: number, collectionId: number) =>
      ['creator-gifting', 'collection-products', brandId, collectionId] as const,
  },

  /**
   * Scheduler query keys.
   */
  scheduler: {
    /** The active user's shareable calendar links */
    shareableCalendarLinks: () => ['scheduler', 'shareable-calendar-links'] as const,

    /** The brand's Publishing Autopilot web article tracker (domain + install snippet) */
    webArticleTracker: (brandId: number | null | undefined) =>
      ['scheduler', 'web-article-tracker', brandId ?? null] as const,

    /** The brand's Publishing Autopilot content rules */
    autopilotRules: (brandId: number | null | undefined) =>
      ['scheduler', 'autopilot-rules', brandId ?? null] as const,

    queueSettingsRoot: () => ['scheduler', 'queue-settings'] as const,

    /** The brand's scheduler settings (queue pause state) */
    queueSettings: (brandId: number | null | undefined) =>
      ['scheduler', 'queue-settings', brandId ?? null] as const,

    /** Queue pause state for a set of brands (multi-brand publishing) */
    queueSettingsForBrands: (brandIds: number[]) =>
      ['scheduler', 'queue-settings-brands', ...brandIds] as const,
  },

  /**
   * Instagram query keys.
   * Scoped by brand ID.
   */
  instagram: {
    /** Root key for all Instagram queries */
    all: () => ['instagram'] as const,

    account: (brandId: number | null | undefined) =>
      ['instagram', 'account', brandId ?? null] as const,

    /** Demographics insights */
    demographics: (brandId: number, params: Record<string, unknown>) =>
      ['instagram', 'demographics', brandId, params] as const,

    /** Account insights */
    insights: (brandId: number, params: Record<string, unknown>) =>
      ['instagram', 'insights', brandId, params] as const,

    /** Brand's recent published posts (scheduler feed preview) */
    posts: (brandId: number | null) => ['instagram', 'posts', brandId] as const,

    /** Single Instagram post (refreshed metrics for the media popup) */
    post: (brandId: number | null | undefined, postSourceId: string | number | null | undefined) =>
      ['instagram', 'post', brandId ?? null, postSourceId ?? null] as const,

    mediaRelationship: (brandId: number | null | undefined, handle: string | null | undefined) =>
      ['instagram', 'media-relationship', brandId ?? null, handle ?? null] as const,
  },

  /**
   * Pinterest query keys.
   * Scoped by brand ID.
   */
  pinterest: {
    /** Root key for all Pinterest queries */
    all: () => ['pinterest'] as const,

    /** Engagement page chart data (all five metrics, one date-range/scale combo) */
    engagementGraphStats: (
      brandId: number | null | undefined,
      startDate: string,
      endDate: string,
      timeScale: string,
    ) =>
      [
        'pinterest',
        'engagement-graph-stats',
        brandId ?? null,
        startDate,
        endDate,
        timeScale,
      ] as const,

    topPins: (
      brandId: number | null | undefined,
      params: { startDate: string; endDate: string; sort: string; limit: number },
    ) => ['pinterest', 'top-pins', brandId ?? null, normalizeFilters(params)] as const,

    overviewTopPins: (
      brandId: number | null | undefined,
      params: { startDate: string; endDate: string; sort: string; limit: number },
    ) => ['pinterest', 'overview-top-pins', brandId ?? null, normalizeFilters(params)] as const,

    /** Net new / total follower timeseries for the Your Followers page */
    followerGraphStats: (
      brandId: number,
      params: { startDate: string; endDate: string; scale: string },
    ) => ['pinterest', 'follower-graph-stats', brandId, normalizeFilters(params)] as const,
  },

  /**
   * Library/Media query keys.
   * Scoped by brand ID.
   */
  library: {
    /** Root key for all library queries */
    all: () => ['library'] as const,

    contentTags: (organizationId: number) => ['library', 'content-tags', organizationId] as const,

    /** Root for every media list of a brand — use as an invalidation prefix */
    mediaRoot: (brandId: number) => ['library', 'media', brandId] as const,

    /** Media list with filters */
    media: (brandId: number, params: Record<string, unknown>) =>
      ['library', 'media', brandId, params] as const,

    /** Root for every media V2 detail of a brand — use as an invalidation prefix */
    mediaDetailRoot: (brandId: number) => ['library', 'media-detail', brandId] as const,

    mediaInsightsSection: (
      brandId: number,
      mediaId: number | string,
      key: string,
      requirement?: object,
    ) =>
      [
        'library',
        'media-insights-section',
        brandId,
        mediaId,
        key,
        ...(requirement ? [requirement] : []),
      ] as const,

    mediaPartnerAdStatus: (brandId: number, mediaId: number | string, key: string) =>
      [...QueryKeys.library.mediaInsightsSection(brandId, mediaId, key), 'status'] as const,

    /** Media V2 detail for a Library media ID — also the invalidation prefix for its enrichment */
    mediaDetail: (brandId: number, mediaId: number) =>
      [...QueryKeys.library.mediaDetailRoot(brandId), mediaId] as const,

    /**
     * Optional-include V2 detail for a Library media ID. `include` is deduped and sorted so
     * equivalent include sets share a cache entry.
     */
    mediaDetailEnrichment: (brandId: number, mediaId: number, include: readonly string[]) =>
      [
        ...QueryKeys.library.mediaDetail(brandId, mediaId),
        [...new Set(include)].toSorted(),
      ] as const,

    /** Top post media list with filters and sorting */
    topPostMediaLists: (brandId: number | null) =>
      ['library', 'top-post-media-list', brandId] as const,

    topPostMediaList: <TFilters>(
      brandId: number | null,
      params: {
        limit: number;
        filters: TFilters | null;
        sortFields: string[] | null;
      },
    ) => [...QueryKeys.library.topPostMediaLists(brandId), params] as const,

    contentRightsCountsRoot: (brandId: number) =>
      ['library', 'content-rights-counts', brandId] as const,

    /** UGC per-rights-status counts for a given filter set (excludes contentRightsType) */
    contentRightsCounts: (brandId: number, filters: Record<string, unknown>) =>
      [...QueryKeys.library.contentRightsCountsRoot(brandId), filters] as const,

    /** Galleries for a brand, scoped by gallery type — use as invalidation prefix */
    galleries: (brandId: number, galleryType: string) =>
      ['library', 'galleries', brandId, galleryType] as const,

    /** Galleries scoped by gallery type and search query — use as the query key */
    galleriesSearch: (brandId: number, galleryType: string, search: string) =>
      ['library', 'galleries', brandId, galleryType, search] as const,

    mediaAddToOptions: (brandId: number, destination?: string) =>
      ['library', 'media-add-to-options', brandId, ...(destination ? [destination] : [])] as const,

    /**
     * Root for the paginated gallery list family and the by-ids lookup.
     * Use as an invalidation prefix on brand change to clear all migrated
     * gallery list/by-ids caches scoped to the previous brand.
     */
    galleriesListRoot: (brandId: number) => ['library', 'gallery-lists', brandId] as const,

    /**
     * Paginated gallery list with the full filter set (default scope).
     * `filters` is normalized so equivalent payloads share a cache entry.
     */
    galleriesList: (brandId: number, filters: Record<string, unknown>) =>
      ['library', 'gallery-lists', brandId, 'list', normalizeFilters(filters)] as const,

    /**
     * Paginated gallery list scoped to the board-index page.
     * Isolated from `galleriesList` so the board-index pagination cursor
     * does not interleave with menu/dropdown gallery list caches.
     */
    galleriesPageList: (brandId: number, filters: Record<string, unknown>) =>
      ['library', 'gallery-lists', brandId, 'page', normalizeFilters(filters)] as const,

    /**
     * Galleries fetched by ID — non-paginated bare-array response.
     * `ids` is sorted so equivalent selections share a cache entry.
     */
    galleriesByIds: (brandId: number, ids: readonly number[]) =>
      ['library', 'gallery-lists', brandId, 'by-ids', [...ids].toSorted((a, b) => a - b)] as const,

    /** Root key for all single-gallery queries — use for broad invalidation */
    galleriesRoot: (brandId: number) => ['library', 'gallery', brandId] as const,

    /** Single gallery detail */
    gallery: (brandId: number, galleryId: number) =>
      ['library', 'gallery', brandId, galleryId] as const,

    /** Summary galleries (bare array, summary=true) for a brand, keyed by filter payload */
    galleriesSummary: (brandId: number, filters: Record<string, unknown>) =>
      ['library', 'galleries-summary', brandId, filters] as const,

    /** Root key for all summary gallery queries — use as invalidation prefix */
    galleriesSummaryAll: () => ['library', 'galleries-summary'] as const,

    /** Root key for all gallery-media queries in a brand — use for broad invalidation */
    galleryMediaRoot: (brandId: number) => ['library', 'gallery-media', brandId] as const,

    /** Gallery media v2 (paginated infinite query) */
    galleryMedia: (
      brandId: number,
      galleryId: number | string,
      filters?: Record<string, unknown>,
    ) =>
      filters
        ? (['library', 'gallery-media', brandId, galleryId, normalizeFilters(filters)] as const)
        : (['library', 'gallery-media', brandId, galleryId] as const),

    /**
     * Multi-brand gallery search results keyed by brand ID.
     * `brandIds` is sorted so equivalent selections share a cache entry.
     * `filters` is normalized for stable key comparison.
     */
    galleriesMultiBrand: (brandIds: number[], filters: Record<string, unknown>) =>
      [
        'library',
        'galleries-multi-brand',
        [...brandIds].toSorted((a, b) => a - b),
        normalizeFilters(filters),
      ] as const,

    /** Root key for all gallery-tag list queries — use as invalidation prefix */
    galleryTagsRoot: (brandId: number) => ['library', 'gallery-tags', brandId] as const,

    /**
     * Gallery tags for a brand, keyed by request params (activeOnly/galleryType/sort).
     * `params` is normalized so equivalent payloads share a cache entry.
     */
    galleryTags: (brandId: number, params: Record<string, unknown>) =>
      ['library', 'gallery-tags', brandId, normalizeFilters(params)] as const,

    /** Media links (media products) attached to a single media item */
    mediaLinks: (brandId: number, mediaId: number | string) =>
      ['library', 'media-links', brandId, mediaId] as const,

    /** UTM settings for a brand, scoped by channel */
    utmSettings: (brandId: number, channel: string) =>
      ['library', 'utm-settings', brandId, channel] as const,

    /** Open Graph metadata for a specific URL */
    urlMetadata: (url: string) => ['library', 'url-metadata', url] as const,

    /** Performance alerts for an organization */
    performanceAlerts: (orgId: number) => ['library', 'performance-alerts', orgId] as const,

    /**
     * Performance-alert-eligible metrics (library-backend), keyed by whether discontinued metrics
     * are included and the (sorted) selected brand IDs. Brand IDs drive server-side feature-flag
     * gating, so the query refetches when the selection changes. Not scoped by org — the catalog
     * doesn't vary by organization.
     */
    eligiblePerformanceAlertMetrics: (includeDeprecated: boolean, brandIds: number[] = []) =>
      ['library', 'performance-alert-eligible-metrics', includeDeprecated, brandIds] as const,

    /** Preview match results for a performance alert configuration */
    performanceAlertPreview: (
      orgId: number,
      brandIds: number[],
      details: Pick<PerformanceAlertDetails, 'thresholds' | 'filters'>,
    ) => ['library', 'performance-alerts-preview', orgId, brandIds, details] as const,

    /** Live galleries stats for a brand, keyed by request params */
    liveGalleriesStats: (brandId: number, params: Record<string, unknown>) =>
      ['library', 'live-galleries-stats', brandId, params] as const,

    /** Root key for all collaborator typeahead searches — cancel/invalidation prefix */
    collaboratorsRoot: () => ['library', 'collaborators'] as const,

    /**
     * Collaborator typeahead search (multi-brand). `brandIds` is sorted and
     * `dateRange` normalized so equivalent selections share a cache entry.
     */
    collaboratorSearch: (
      brandIds: number[],
      searchQuery: string,
      dateRange: { startDate?: string; endDate?: string } = {},
    ) =>
      [
        'library',
        'collaborators',
        'search',
        [...brandIds].toSorted((a, b) => a - b),
        searchQuery,
        normalizeFilters(dateRange),
      ] as const,

    /** Single-brand collaborator wildcard search */
    collaboratorWildcardSearch: (brandId: number, searchQuery: string) =>
      ['library', 'collaborators', 'wildcard-search', brandId, searchQuery] as const,
  },

  /** Library intelligence (vision service) query keys, scoped by brand ID. */
  libraryIntelligence: {
    /** Root key for all library intelligence queries */
    all: () => ['library-intelligence'] as const,

    /** Average star improvement for a brand over the last year */
    starImprovement: (brandId: number | null) =>
      ['library-intelligence', 'star-improvement', brandId] as const,
  },

  /**
   * Constellation Pro query keys.
   * Scoped by brand-level identifiers.
   */
  constellationPro: {
    /** Root key for all Constellation Pro queries */
    all: () => ['constellation-pro'] as const,

    /** Brand Guidelines record (assets + additional instructions) for a brand */
    brandGuidelines: (brandId: number | null) =>
      ['constellation-pro', 'brand-guidelines', brandId] as const,

    /**
     * Prefix shared by every project-scoped entry for a brand: the projects list and picker
     * (`projectsList`) and each project's own subtree (`projectRoot`). Walk it to find every
     * project's asset lists when the project holding an asset isn't known up front.
     */
    projectsRoot: (brandId: number | null) => ['constellation-pro', 'projects', brandId] as const,

    /**
     * Prefix shared by every projects-list cache entry regardless of filters. Invalidate with
     * this, not `projects()` — Pinia Colada's non-exact match is element-by-element, so
     * `projects(brandId)` (whose filters element normalizes to `{}`) does not match a cache
     * entry keyed with an actual search term.
     */
    projectsList: (brandId: number | null) =>
      [...QueryKeys.constellationPro.projectsRoot(brandId), 'list'] as const,

    /** Projects list for a brand, sorted by last message time, optionally filtered by search */
    projects: (brandId: number | null, filters: Record<string, unknown> = {}) =>
      [...QueryKeys.constellationPro.projectsList(brandId), normalizeFilters(filters)] as const,

    /**
     * Distinct from `projects()` — that one single-pages (Projects page, secondary nav); this
     * infinite-scrolls (the Add to Project picker) and would otherwise collide on the same key
     * with an incompatible cache shape (one page vs. an array of pages) for the same filters.
     */
    projectsPicker: (brandId: number | null, filters: Record<string, unknown> = {}) =>
      [
        ...QueryKeys.constellationPro.projectsList(brandId),
        'picker',
        normalizeFilters(filters),
      ] as const,

    /**
     * Root key for one project's detail, messages and assets. The list lives outside this
     * prefix so invalidating a project doesn't refetch every mounted chat via the list.
     */
    projectRoot: (brandId: number | null, projectId: number | null) =>
      [...QueryKeys.constellationPro.projectsRoot(brandId), projectId] as const,

    /** Members of one project */
    projectMembers: (brandId: number | null, projectId: number | null) =>
      [...QueryKeys.constellationPro.projectRoot(brandId, projectId), 'members'] as const,

    /** Project detail, including its chats */
    project: (brandId: number | null, projectId: number | null) =>
      ['constellation-pro', 'projects', brandId, projectId, 'detail'] as const,

    /**
     * One chat's message history. Nested project → chat → messages so a socket patch
     * invalidates exactly one chat and leaves sibling chat caches untouched.
     */
    chatMessages: (brandId: number | null, projectId: number | null, chatId: number | null) =>
      ['constellation-pro', 'projects', brandId, projectId, 'chats', chatId, 'messages'] as const,

    /**
     * Prefix shared by every filter variant of one project's assets. Invalidate/patch with
     * this, not `projectAssets()` — Colada's non-exact match is element-by-element, so a
     * normalized-empty-filters key would miss an entry keyed by an actual role filter.
     */
    projectAssetsRoot: (brandId: number | null, projectId: number | null) =>
      [...QueryKeys.constellationPro.projectRoot(brandId, projectId), 'assets'] as const,

    /** Assets attached to one project, filterable by role/chat */
    projectAssets: (
      brandId: number | null,
      projectId: number | null,
      filters: Record<string, unknown> = {},
    ) =>
      [
        ...QueryKeys.constellationPro.projectAssetsRoot(brandId, projectId),
        normalizeFilters(filters),
      ] as const,

    /**
     * Prefix shared by every filter variant of the brand-wide AI asset library. Invalidate/patch
     * with this, not `aiAssets()` — Colada's non-exact match is element-by-element, so a
     * normalized-empty-filters key would miss an entry keyed by an actual filter variant.
     */
    aiAssetsRoot: (brandId: number | null) => ['constellation-pro', 'ai-assets', brandId] as const,

    /** Brand-user-wide AI asset library, filterable by role/type/favourite */
    aiAssets: (brandId: number | null, filters: Record<string, unknown> = {}) =>
      [...QueryKeys.constellationPro.aiAssetsRoot(brandId), normalizeFilters(filters)] as const,
  },

  /**
   * Dashboards query keys.
   * Scoped by brand-level identifiers.
   */
  dashboards: {
    adContentDetailRoot: () => ['dashboards', 'ad-content-detail'] as const,
    adContentDetail: (scope: AdContentDetailScope, identity: AdContentDetailIdentity) =>
      [
        ...QueryKeys.dashboards.adContentDetailRoot(),
        {
          brandIds: [...new Set(scope.brandIds)].toSorted((a, b) => a - b),
          sourceAdAccountIds: [...new Set(scope.sourceAdAccountIds)].toSorted((a, b) =>
            a.localeCompare(b),
          ),
          startDate: scope.startDate,
          endDate: scope.endDate,
          filters: normalizeFilters(
            Object.fromEntries(
              Object.entries(scope.filters).map(([key, value]) => [
                key,
                Array.isArray(value)
                  ? [...new Set(value)].toSorted((a, b) => a.localeCompare(b))
                  : value,
              ]),
            ),
          ),
        },
        identity,
      ] as const,
    adPicker: (picker: string, scope: Record<string, unknown>) =>
      ['dashboards', 'ad-picker', picker, normalizeFilters(scope)] as const,
    /** Root key for all dashboard queries */
    all: () => ['dashboards'] as const,

    /** KAWO embed URL for a specific brand */
    kawoEmbed: (kawoBrandId: string) => ['dashboards', 'kawo-embed', kawoBrandId] as const,

    /** Metric reference metadata (display names, data types, flags) — rarely changes */
    metricDetails: () => ['dashboards', 'metric-details'] as const,

    /**
     * Curated template presets with their access issues. Brand is in the key because the
     * endpoint evaluates access against the brands it is given, so two brands can see
     * the same preset with different issues.
     */
    templatePresets: (brandId: number) => ['dashboards', 'template-presets', brandId] as const,

    /** Shareable-link allowance for one dashboard/user pair — the cap is per pair */
    shareableLinkUsage: (dashboardId: number, userId: number) =>
      ['dashboards', 'shareable-link-usage', dashboardId, userId] as const,

    /**
     * Prefix for the three requests that make up one dashboard: the dashboard
     * itself plus its sharing list and scheduled exports. Use with
     * `exact: false` to invalidate all three at once.
     */
    dashboardRoot: (dashboardId: number) => ['dashboards', 'dashboard', dashboardId] as const,

    /** Prefix for those same three requests across every dashboard. */
    allDashboardsRoot: () => ['dashboards', 'dashboard'] as const,

    /** The dashboard itself: name, layout, brand_ids, channels. */
    detail: (dashboardId: number) => ['dashboards', 'dashboard', dashboardId, 'detail'] as const,

    /** Sharing list for a dashboard. */
    users: (dashboardId: number) => ['dashboards', 'dashboard', dashboardId, 'users'] as const,

    /** Scheduled exports for a dashboard. */
    autoExports: (dashboardId: number) =>
      ['dashboards', 'dashboard', dashboardId, 'auto-exports'] as const,

    /**
     * Prefix for every cached report query within a dashboard. Use as `key` with
     * `exact: false` to reach all filter variants of one or more reports —
     * narrow to specific reports with a `predicate` on the `[3]` reportId slot.
     * Filter changes need no invalidation; they mint a new key on their own.
     */
    reportRoot: (dashboardId: number) => ['dashboards', 'report', dashboardId] as const,

    /** Prefix for cached report queries across every dashboard. */
    allReportsRoot: () => ['dashboards', 'report'] as const,

    /**
     * Single report data, filter-aware. Filters MUST be in the key so each
     * variant occupies its own cache slot — toggling filters swaps entries
     * rather than overwriting.
     *
     * Key positions (used by invalidation predicates):
     *   [0]='dashboards', [1]='report', [2]=dashboardId, [3]=reportId, [4]=filters
     */
    report: (dashboardId: number, reportId: number, filters: Record<string, unknown>) =>
      ['dashboards', 'report', dashboardId, reportId, filters] as const,

    adContentPages: (
      dashboardId: number | undefined,
      reportId: number | undefined,
      scope: Record<string, unknown>,
    ) =>
      [
        'dashboards',
        'report',
        dashboardId ?? null,
        reportId ?? null,
        'ad-content-pages',
        scope,
      ] as const,

    /**
     * Channel analytics overview sections. Each section owns its own query, so more than one
     * wrapper can ask for the same data — Colada dedupes on these keys.
     */
    overview: {
      /** Root key for all overview queries */
      all: () => ['dashboards', 'overview'] as const,

      paidReportIssues: (brandId: number, range: OverviewKeyRange) =>
        [
          'dashboards',
          'overview',
          'instagram-paid-report-issues',
          brandId,
          normalizeFilters(range),
        ] as const,

      /**
       * Top-line summary stats for a channel's overview header. `metrics` is part of the key
       * because it's part of the request — see `timeseries` below.
       */
      topLine: (
        channel: string,
        brandId: number,
        range: OverviewKeyRange,
        metrics: string[],
        authorship?: readonly string[],
      ) =>
        [
          'dashboards',
          'overview',
          'top-line',
          channel,
          brandId,
          normalizeFilters(range),
          sortMetrics(metrics),
          // Appended only when set, so channels without one keep their existing keys.
          ...(authorship ? [sortMetrics([...authorship])] : []),
        ] as const,

      /**
       * Per-metric timeseries backing the overview charts. Two sections on the same
       * channel/brand/window/scale can request different metric sets — a followers chart and an
       * account-performance chart — so the set has to be in the key. Without it they resolve to
       * one entry and the second section reads a bag holding none of its series.
       */
      timeseries: (
        channel: string,
        brandId: number,
        range: OverviewKeyRange,
        scale: string,
        metrics: string[],
        authorship?: readonly string[],
      ) =>
        [
          'dashboards',
          'overview',
          'timeseries',
          channel,
          brandId,
          normalizeFilters(range),
          scale,
          sortMetrics(metrics),
          // Appended only when set, so channels without one keep their existing keys.
          ...(authorship ? [sortMetrics([...authorship])] : []),
        ] as const,

      /**
       * A content section issues two requests against the same source and sort metric: the
       * aggregate drawn as the average line, and the posts themselves. They are keyed apart
       * because they resolve to different shapes — a number and a media list — and a shared key
       * would hand one section the other's payload.
       */
      content: {
        /** Aggregate stats for a content section, scoped to its source and sort metric */
        stats: (
          channel: string,
          brandId: number,
          range: OverviewKeyRange,
          source: string,
          sort: string,
        ) =>
          [
            'dashboards',
            'overview',
            'content',
            'stats',
            channel,
            brandId,
            normalizeFilters(range),
            source,
            sort,
          ] as const,

        /**
         * Posts backing a content section. Carries `sortOrder`, `limit` and `mediaTypes` on top of
         * the stats key's dimensions: all three change which posts come back, while none moves the
         * period average the stats query returns.
         */
        media: (
          channel: string,
          brandId: number,
          range: OverviewKeyRange,
          source: string,
          sort: string,
          sortOrder: string,
          limit: number,
          mediaTypes?: readonly string[],
        ) =>
          [
            'dashboards',
            'overview',
            'content',
            'media',
            channel,
            brandId,
            normalizeFilters(range),
            source,
            sort,
            sortOrder,
            limit,
            mediaTypes ? [...mediaTypes] : null,
          ] as const,
      },
    },
  },

  overviewDemographics: {
    all: () => ['overview-demographics'] as const,

    channel: (channel: string, brandId: number | undefined, date: string | undefined) =>
      ['overview-demographics', channel, brandId ?? null, date ?? null] as const,
  },

  /**
   * Landing page query keys.
   * Scoped by brand ID.
   */
  lp: {
    /** Root key for all landing page queries */
    all: () => ['lp'] as const,

    contentReport: (
      brandId: number | undefined,
      configId: string,
      filters: Record<string, unknown>,
      sortFields: string[],
    ) =>
      [
        'lp',
        'content-report',
        brandId ?? null,
        configId,
        normalizeFilters(filters),
        sortFields,
      ] as const,

    /** Benchmark graph stats for TSI benchmarks */
    benchmarkGraphStats: (
      brandId: number,
      dateRange: { startDate: string; endDate: string },
      channels: string[],
      benchmarks: Array<{ industry_id: number; follower_bucket: number; type: 'industry' }>,
    ) => ['lp', 'benchmark-graph-stats', brandId, dateRange, channels, benchmarks] as const,

    /**
     * Current-period graph stats for one landing page metric. `hasMetaAds` is part of the key
     * because it selects the real metric each channel is fetched under (see `getGraphAlias`).
     */
    graphStats: (
      brandId: number,
      metric: string,
      dateRange: { startDate: string; endDate: string },
      channels: string[],
      timeScale: string,
      hasMetaAds: boolean,
    ) =>
      ['lp', 'graph-stats', brandId, metric, dateRange, channels, timeScale, hasMetaAds] as const,

    /** TSI line graph stats — the connected TSI channels and the UGC channels as one series */
    tsiLineGraphStats: (
      brandId: number,
      dateRange: { startDate: string; endDate: string },
      tsiChannels: string[],
      ugcChannels: string[],
    ) => ['lp', 'tsi-line-graph-stats', brandId, dateRange, tsiChannels, ugcChannels] as const,

    /** TSI doughnut graph stats over the combined TSI + UGC channels */
    tsiDoughnutGraphStats: (
      brandId: number,
      dateRange: { startDate: string; endDate: string },
      channels: string[],
    ) => ['lp', 'tsi-doughnut-graph-stats', brandId, dateRange, channels] as const,

    /**
     * Top-line stats tile (per-channel breakdown + avg engagement + TSI totals).
     * `hasMetaAds` and `youTubeMetricsAllowed` both belong in the key — they change which
     * metrics `getTopLineMetricList` requests per channel. `timezone` belongs in the key too —
     * it changes the UTC date boundaries the request converts to, so a timezone change (e.g. a
     * profile edit mid-session) is a cache miss, not a request the stale cached window answers.
     */
    topLineStats: (
      brandId: number,
      reportDateRange: [string, string],
      contextDateRange: [string, string],
      tsiStartDate: string,
      channels: string[],
      hasMetaAds: boolean,
      youTubeMetricsAllowed: boolean,
      /** `null`, not `undefined` — the key must stay JSON-serializable. */
      timezone: string | null,
    ) =>
      [
        'lp',
        'top-line-stats',
        brandId,
        reportDateRange,
        contextDateRange,
        tsiStartDate,
        channels,
        hasMetaAds,
        youTubeMetricsAllowed,
        timezone,
      ] as const,
  },

  /**
   * TikTok query keys.
   * Scoped by brand ID.
   */
  tiktok: {
    videoViews: (
      brandId: number,
      mediaId: number | string,
      sourceId: string,
      range: { startDate: string; endDate: string; timeScale: string },
    ) => ['tiktok', 'video-views', brandId, mediaId, sourceId, range] as const,
    /** Root key for all TikTok queries */
    all: () => ['tiktok'] as const,

    /** Trending sounds with filters */
    trendingSounds: (
      brandId: number,
      filters: { dateRange: string; country: string; genre: string },
    ) => ['tiktok', 'trending-sounds', brandId, filters] as const,

    /**
     * Trending videos for a window. Outside `QueryKeys.dashboards` because tiktok-backend serves
     * it. No sort dimension — the endpoint ranks server-side and takes no sort parameter.
     */
    trendingVideos: (
      brandId: number,
      params: { startDate: string; endDate: string; limit: number },
    ) => ['tiktok', 'trending-videos', brandId, normalizeFilters(params)] as const,

    /** Follower demographics as of a single day, not a window */
    followerDemographics: (brandId: number, date: string) =>
      ['tiktok', 'follower-demographics', brandId, date] as const,

    availableUgcHashtags: (brandId: number) =>
      ['tiktok', 'ugc-hashtags', 'available', brandId] as const,

    monitoredUgcHashtags: (brandId: number) =>
      ['tiktok', 'ugc-hashtags', 'monitored', brandId] as const,
  },

  /**
   * YouTube query keys.
   */
  youtube: {
    /** Root key for all YouTube queries */
    all: () => ['youtube'] as const,

    /** Parent key for all channel-info entries — used to filter queryCache by prefix. */
    channelInfo: () => ['youtube', 'channel-info'] as const,

    /** Channel info by source channel ID — used to resolve the channel handle (custom_url). */
    channelInfoBySourceId: (sourceChannelId: string) =>
      ['youtube', 'channel-info', sourceChannelId] as const,

    /**
     * Trending videos for a window. Outside `QueryKeys.dashboards` because youtube-backend serves
     * it. No sort dimension — the endpoint ranks server-side and takes no sort parameter.
     */
    trendingVideos: (
      brandId: number,
      params: { startDate: string; endDate: string; limit: number },
    ) => ['youtube', 'trending-videos', brandId, normalizeFilters(params)] as const,
  },

  /**
   * Community query keys.
   * Scoped by brand ID(s) and filter payload.
   */
  community: {
    /** Root key for all community queries */
    all: () => ['community'] as const,

    /** Team Activity rollup (POST /team_activities) keyed by full filter payload */
    teamActivities: (payload: Record<string, unknown>) =>
      ['community', 'team-activities', payload] as const,

    /** Resolve a brand's INSTAGRAM_CONVERSATION row for a given creator handle (exact match) */
    dmByHandle: (brandId: number, handle: string) =>
      ['community', 'dm-by-handle', brandId, handle] as const,

    /** AI prompt categories that a brand has set up to use in filters */
    aiCategories: (brandId: number) => ['community', 'ai-categories', brandId] as const,

    /** Saved replies for one brand */
    presetReplies: (brandId: number) => ['community', 'preset-replies', brandId] as const,

    /** Message tags across brands (GET /tags), used to resolve tag names */
    messageTagsRoot: () => ['community', 'message-tags'] as const,
    messageTags: (brandIds: number[]) => ['community', 'message-tags', brandIds] as const,

    settingsTagsRoot: () => ['community', 'settings-tags'] as const,
    settingsTags: (params: Record<string, unknown>) =>
      ['community', 'settings-tags', normalizeFilters(params)] as const,
    settingsCategoriesRoot: () => ['community', 'settings-categories'] as const,
    settingsCategories: (brandIds: number[]) =>
      ['community', 'settings-categories', brandIds] as const,
    settingsCategoryTable: (params: Record<string, unknown>) =>
      ['community', 'settings-categories', 'table', normalizeFilters(params)] as const,
    settingsTagCatalogRoot: () => ['community', 'settings-tags', 'catalog'] as const,
    settingsTagCatalog: (brandIds: number[]) =>
      ['community', 'settings-tags', 'catalog', brandIds] as const,
    settingsTaggingTasksRoot: () => ['community', 'settings-tagging-tasks'] as const,
    settingsTaggingTasks: (brandIds: number[]) =>
      ['community', 'settings-tagging-tasks', brandIds] as const,

    /** Assignable users across brands (GET /assignees) */
    assignees: (brandIds: number[]) => ['community', 'assignees', brandIds] as const,

    /** Sample of interactions matching a draft custom view's filters */
    interactionsPreview: (params: Record<string, unknown>) =>
      ['community', 'interactions-preview', params] as const,

    /** Relationship stats and LLM summary for one conversation participant */
    participantInsights: (
      brandId: number | null | undefined,
      participantId: number | null | undefined,
    ) => ['community', 'participant-insights', brandId ?? null, participantId ?? null] as const,

    /** Sentiment and spam definitions and learning toggles for a set of brands */
    classificationSettings: (brandIds: number[]) =>
      ['community', 'classification-settings', ...brandIds] as const,
  },

  /**
   * Release notes query keys.
   * Global (not scoped by brand).
   */
  releaseNotes: {
    /** Root key for all release notes queries */
    all: () => ['release-notes'] as const,

    /** Release notes list */
    list: () => ['release-notes', 'list'] as const,
  },

  /**
   * Information Request Form query keys.
   * Scoped by token.
   */
  informationRequest: {
    /** Root key for all IRF queries */
    all: () => ['information-request'] as const,

    /** Form data for a specific token */
    form: (token: string) => ['information-request', 'form', token] as const,
  },

  /**
   * Insights query keys.
   * Brand-scoped for the insight chip; organization-scoped for social listening.
   */
  insights: {
    /** Root key for all insights queries */
    all: () => ['insights'] as const,

    /** Active insights list for one or more brands */
    list: (brandIds: number[]) =>
      ['insights', 'list', [...brandIds].toSorted((a, b) => a - b)] as const,

    /** Active insights list for one organization */
    listByOrganization: (organizationId: number) =>
      ['insights', 'list-by-organization', organizationId] as const,
  },

  /**
   * Partnership DMs query keys.
   * Scoped by brand, source, and creator's source user id.
   */
  partnerships: {
    /** Root key for all partnership queries */
    all: () => ['partnerships'] as const,

    /** Thread for a creator (without cursor — pagination is via infinite query pages) */
    messages: (brandId: number, source: string, sourceCreatorId: string) =>
      ['partnerships', 'messages', brandId, source, sourceCreatorId] as const,

    /** Conversation metadata (PARTNERSHIP + GENERAL DM rows) for a creator */
    conversations: (brandId: number, source: string, sourceCreatorId: string) =>
      ['partnerships', 'conversations', brandId, source, sourceCreatorId] as const,

    /** Message templates for outreach (scoped to brand + source) */
    messageTemplates: (brandId: number, source: string) =>
      ['partnerships', 'message-templates', brandId, source] as const,
  },

  /**
   * Products query keys (product store migration).
   * All product data is brand-scoped — brand id in every key replaces the
   * old store's `resetOnBrandChange: true`.
   */
  products: {
    /** Root key for all products queries — use for broad invalidation */
    all: () => ['products'] as const,

    /** Root for all product-list pages of a brand (cache walk / invalidation) */
    listRoot: (brandId: number) => ['products', 'list', brandId] as const,

    /**
     * Paginated product list for a brand.
     * `filters` is normalized so equivalent payloads share a cache entry.
     */
    list: (brandId: number, filters: Record<string, unknown>) =>
      ['products', 'list', brandId, normalizeFilters(filters)] as const,

    /**
     * Brand-has-any-products probe (`limit: 1` existence check).
     * Lives under `listRoot` so list invalidation refreshes it, but the
     * string discriminator can never collide with a `list` filters object.
     */
    hasProducts: (brandId: number) => ['products', 'list', brandId, 'has-products'] as const,

    /** Single product detail */
    detail: (brandId: number, productId: number | string) =>
      ['products', 'detail', brandId, productId] as const,

    /** Product feeds for a brand, keyed by request params (sort) */
    feeds: (brandId: number, params: Record<string, unknown> = {}) =>
      ['products', 'feeds', brandId, normalizeFilters(params)] as const,

    /** Brand-level products click stats (scale/date range) */
    analytics: (brandId: number, params: Record<string, unknown>) =>
      ['products', 'analytics', brandId, normalizeFilters(params)] as const,

    /** Per-product click stats (scale/date range/source) */
    clicks: (brandId: number, productId: number | string, params: Record<string, unknown>) =>
      ['products', 'clicks', brandId, productId, normalizeFilters(params)] as const,

    /** Media attached to a product */
    media: (brandId: number, productId: number | string) =>
      ['products', 'media', brandId, productId] as const,

    /** Open Graph metadata lookups made from the product link editor */
    urlMetadata: (brandId: number, url: string) =>
      ['products', 'url-metadata', brandId, url] as const,
  },

  /**
   * Intuitive Topic Creation (NLS) query keys.
   * Scoped by organization ID.
   */
  intuitiveTopicCreation: {
    /** Root key for all intuitive topic creation queries */
    all: () => ['intuitive-topic-creation'] as const,

    /** Example prompts for an organization */
    examplePrompts: (orgId: number) =>
      ['intuitive-topic-creation', 'example-prompts', orgId] as const,

    /** Raw parse tree of a proposition keywords expression */
    parseTree: (orgId: number, expression: string) =>
      ['intuitive-topic-creation', 'parse-tree', orgId, expression] as const,

    /** Batch stats for all trending topic cards in a section (daily cache) */
    trendingTopicAllCardStats: (orgId: number, keywordsExpressions: string[], date: string) =>
      [
        'intuitive-topic-creation',
        'trending-topic-all-card-stats',
        orgId,
        keywordsExpressions,
        date,
      ] as const,
  },

  /**
   * Creator portal queries.
   * Scoped by the creator's long-lived portal link token — there is no brand or user in scope.
   */
  creatorPortal: {
    /** Root key for all creator portal queries */
    all: () => ['creator-portal'] as const,

    /** One bucket (todo / completed / expired) of the creator's gifting campaigns */
    giftingCampaigns: (token: string, type: string) =>
      ['creator-portal', 'gifting-campaigns', token, type] as const,

    /** Every bucket of a creator's gifting campaigns, for invalidating all of them at once */
    giftingCampaignsAll: (token: string) => ['creator-portal', 'gifting-campaigns', token] as const,

    /** A campaign's gifting collections and their selectable products */
    campaignCollections: (token: string, campaignId: number) =>
      ['creator-portal', 'gifting-collections', token, campaignId] as const,

    /** The creator's stored order for one gifting campaign */
    giftingCampaignOrder: (token: string, campaignId: number) =>
      ['creator-portal', 'gifting-campaign-order', token, campaignId] as const,

    /** The creator's stored shipping details, used to pre-fill the shipping form */
    shippingDetails: (token: string) => ['creator-portal', 'shipping-details', token] as const,
  },

  /** OCAPI (Salesforce Commerce Cloud) gifting integration queries. */
  ocapi: {
    /** Root key for all OCAPI queries */
    all: () => ['ocapi'] as const,

    /** Status of a brand's Data API and Shop API connections */
    connection: (brandId: number) => ['ocapi', 'connection', brandId] as const,

    /** The brand's SFCC category taxonomy (our DB copy, not a live catalog read) */
    categories: (brandId: number) => ['ocapi', 'categories', brandId] as const,

    /**
     * Paginated SFCC product search.
     * `filters` is key-sorted with `undefined` dropped; `null` is kept, so pass
     * `undefined` for an absent filter.
     */
    productSearch: (brandId: number, filters: Record<string, unknown>) =>
      ['ocapi', 'product-search', brandId, normalizeFilters(filters)] as const,

    /** A master product with its variation groups and variants */
    product: (brandId: number, productId: string) =>
      ['ocapi', 'product', brandId, productId] as const,
  },
} as const;

/**
 * Type helper to extract the return type of a query key factory.
 * Useful for typing query key parameters.
 *
 * @example
 * type TopicStatsKey = QueryKeyType<typeof QueryKeys.sl.topicStats>;
 * // readonly ['sl', 'topic-stats', number, number, Record<string, unknown>]
 */
export type QueryKeyType<T extends (...args: never[]) => readonly unknown[]> = ReturnType<T>;
